import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import LoadingSvg2 from "./LoadingSvg2";
import SimpleDialog from "./dialogs/SimpleDialog";
import Toolbar from "./Toolbar";
import UserDetails from "@utils/UserDetails";
import WelcomeHeader from "./header/WelcomeHeader";
import { translate } from "./translations/Translations";
import { useGlobalContext } from "./contextProvider/ContextProvider";

interface RbAProps {
    allowedRoles: string[];
    children: React.JSX.Element;
    redirect?: string;
}

/**
 * Grab the username from the GlobalContext and query the user details API for
 * user roles.
 * If the user has the roles passed in by the calling component, its children
 * are displayed.
 * @name RbA
 */
const RbA = ({ allowedRoles, children, redirect }: RbAProps): React.JSX.Element | null => {
    const { state } = useGlobalContext();
    const { lang, userDetailsError } = state || {};

    const [hasRole, setHasRole] = useState(false);
    const [rbaLoader, setRbaLoader] = useState(true);

    const userDetails = UserDetails();

    /**
     * Retrieve an array of the current user's roles from the user_details
     * sessionStorage. If we successfully retrieve that data, we create the
     * localStorage item, and re-render
     * @name getRoles
     * @returns {[]|{validator, value: []}|{validator: *, value: []}|[string]|{}}
     */
    const getRoles = useCallback(() => {
        if (userDetails) {
            return userDetails.roleDtos;
        }

        return [];
    }, [userDetails]);

    /**
     * Does the user have the correct role to view this page?
     * @name hasRoles
     * @returns {boolean} hasRole
     */
    const hasRoles = useCallback(() => {
        const roleList = getRoles();
        // Get a Set of role strings from the user's role DTOs for O(1) lookups
        const roles = new Set(roleList.map((roleDto: { role: string }) => roleDto.role));

        // Use .some() with .has() for a more performant check to see if a matching role exists
        const hasMatch = allowedRoles.some((allowedRole) => roles.has(allowedRole));
        setHasRole(hasMatch);
    }, [allowedRoles, getRoles]);

    useEffect(() => {
        if (userDetails) {
            setRbaLoader(false);
            hasRoles();
        } else if (userDetailsError) {
            setRbaLoader(false);
        }
    }, [hasRoles, userDetails, userDetailsError, setRbaLoader]);

    if (userDetailsError) {
        return (
            <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
                <WelcomeHeader />
                <Toolbar label={<>&nbsp;</>} />
                <div className="gutter-96">
                    <SimpleDialog
                        message1={translate("Error_UserDetails_Title", lang)}
                        message2={translate("Error_UserDetails_Sub", lang)}
                        message3={
                            <div className="guardian-no-student-details">
                                <div className="info-section">
                                    <p>{translate("Error_UserDetails_Sync_Msg", lang)}</p>
                                    <p style={{ marginTop: "12px" }}>
                                        {translate("Error_UserDetails_Contact_Pre", lang)}
                                    </p>
                                </div>
                            </div>
                        }
                    />
                </div>
            </div>
        );
    }

    if (hasRole) {
        return children;
    }

    // forbid access while loading
    return rbaLoader ? <LoadingSvg2 /> : <Navigate to={redirect ?? "/"} />;
};

export default RbA;
